import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import {
  FullTextRetrievalService,
  FullTextPaper,
} from '../../../services/full-text-retrieval.service';
import { ProjectService } from '../../../services/project.service';
import { ProfileService } from '../../../services/profile.service';
import { ToastService } from '../../../services/toast.service';
import { Project } from '../../../dtos/project';
import { UserInfo } from '../../../dtos/user-info';
import { HttpClient } from '@angular/common/http';
import { Globals } from '../../../global/globals';
import { Group } from '../../../dtos/group';
import { GroupService } from '../../../services/group.service';
import { ProjectState } from '../../../dtos/project-state';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-project-full-text-retrieval',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxExtendedPdfViewerModule, ConfirmModalComponent],
  templateUrl: './project-full-text-retrieval.component.html',
  styleUrls: ['./project-full-text-retrieval.component.scss'],
})
export class ProjectFullTextRetrievalComponent implements OnInit, OnDestroy {
  projectId = '';
  project: Project | null = null;
  group: Group | null = null;
  papers: FullTextPaper[] = [];
  filteredPapers: FullTextPaper[] = [];
  userInfo: UserInfo | null = null;
  readonly ProjectState = ProjectState;

  loading = true;
  submitting = false;
  advanceConfirmOpen = false;
  searchText = '';
  private searchDebounceHandle?: ReturnType<typeof setTimeout>;
  statusFilter = 'ALL'; // ALL, PENDING, DOWNLOADING, MISSING, AWAITING_VERIFICATION, VERIFIED, UNAVAILABLE, FAILED

  // Pagination state
  currentPageIndex = 0;
  currentPageSize = 25;
  totalElements = 0;
  totalPages = 0;
  pageSizeOptions = [10, 25, 50, 100];

  // Project-wide progress counters (independent of page slices)
  totalProjectPapers = 0;
  totalResolvedPapers = 0;
  totalVerifiedPapers = 0;
  totalUnavailablePapers = 0;
  overallProgressPercentage = 0;

  // Workspace / Split-screen Verification view
  selectedPaper: FullTextPaper | null = null;
  pdfUrl: Blob | string | null = null;
  dragOver = false;
  uploadingPdf = false;
  loadingPdf = false;

  private sub?: Subscription;
  private pollInterval?: ReturnType<typeof setInterval>;
  private destroyed = false;
  private isPollingInFlight = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private retrievalService: FullTextRetrievalService,
    private projectService: ProjectService,
    private profileService: ProfileService,
    private toastService: ToastService,
    private httpClient: HttpClient,
    private globals: Globals,
    private groupService: GroupService,
  ) {}

  ngOnInit(): void {
    this.sub = this.route.parent?.params.subscribe((params) => {
      this.projectId = params['projectId'];
      if (!this.projectId) {
        this.router.navigate(['/projects']);
        return;
      }
      this.loadProjectDetails();
      this.loadUserInfo();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sub?.unsubscribe();
    this.clearPollInterval();
    clearTimeout(this.searchDebounceHandle);
    if (this.selectedPaper) {
      this.releaseLock(this.selectedPaper);
    }
  }

  private clearPollInterval(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
  }

  private startPollIfNeeded(): void {
    const hasActive = this.papers.some((p) => p.status === 'PENDING' || p.status === 'DOWNLOADING');
    if (hasActive && !this.pollInterval) {
      this.pollInterval = setInterval(() => {
        if (!this.isPollingInFlight && !this.destroyed) {
          this.isPollingInFlight = true;
          this.loadPapers();
        }
      }, 5000);
    } else if (!hasActive) {
      this.clearPollInterval();
    }
  }

  private loadUserInfo(): void {
    this.profileService.getCurrentUser().subscribe({
      next: (user) => {
        this.userInfo = user;
      },
      error: (err) => {
        console.error('Error loading user info:', err);
      },
    });
  }

  loadProjectDetails(): void {
    this.loading = true;
    this.projectService.getProjectById(this.projectId).subscribe({
      next: (proj) => {
        this.project = proj;
        this.loadOverallProgress();
        this.loadPapers();

        this.groupService.getGroupById(proj.groupId).subscribe({
          next: (grp) => {
            this.group = grp;
          },
          error: (err) => {
            console.error('Error loading group details:', err);
          },
        });
      },
      error: (err) => {
        console.error('Error loading project details:', err);
        this.toastService.error('Failed to load project details', 'Please try again.');
        this.loading = false;
      },
    });
  }

  loadOverallProgress(): void {
    this.retrievalService.getPapers(this.projectId, 0, 999999).subscribe({
      next: (page: any) => {
        const allItems = page.items;
        this.totalProjectPapers = allItems.length;
        this.totalVerifiedPapers = allItems.filter((p: any) => p.status === 'VERIFIED').length;
        this.totalUnavailablePapers = allItems.filter(
          (p: any) => p.status === 'UNAVAILABLE',
        ).length;
        this.totalResolvedPapers = this.totalVerifiedPapers + this.totalUnavailablePapers;
        this.overallProgressPercentage =
          this.totalProjectPapers === 0
            ? 0
            : Math.round((this.totalResolvedPapers / this.totalProjectPapers) * 100);
      },
      error: (err: any) => {
        console.error('Error loading overall retrieval progress:', err);
      },
    });
  }

  loadPapers(): void {
    this.isPollingInFlight = true;
    this.retrievalService
      .getPapers(
        this.projectId,
        this.currentPageIndex,
        this.currentPageSize,
        'title,asc',
        this.searchText,
        this.statusFilter,
      )
      .subscribe({
        next: (page: any) => {
          if (this.destroyed) return;
          this.isPollingInFlight = false;
          this.papers = [...page.items];
          this.filteredPapers = [...page.items];
          this.totalElements = page.totalItems;
          this.totalPages = page.totalPages;
          this.loading = false;

          // Keep selected paper updated in detail view if active
          if (this.selectedPaper) {
            const updated = this.papers.find((p: any) => p.id === this.selectedPaper?.id);
            if (updated) {
              this.selectedPaper = updated;
              this.updatePdfUrl();
            }
          }

          this.startPollIfNeeded();
        },
        error: (err: any) => {
          if (this.destroyed) return;
          this.isPollingInFlight = false;
          console.error('Error loading retrieval papers:', err);
          this.toastService.error('Failed to load papers list', 'Please try again.');
          this.loading = false;
        },
      });
  }

  retryDownload(paper: FullTextPaper): void {
    this.retrievalService.retryDownload(this.projectId, paper.id).subscribe({
      next: () => {
        if (this.destroyed) return;
        this.toastService.info('Download Retried', 'PDF download has been re-queued.');
        this.loadPapers();
      },
      error: (err: any) => {
        if (this.destroyed) return;
        console.error('Error retrying download:', err);
        this.toastService.error('Retry Failed', 'Could not retry PDF download.');
      },
    });
  }

  applyFilters(): void {
    this.currentPageIndex = 0;
    this.loadPapers();
  }

  onSearchChange(): void {
    // Debounce search-as-you-type so we do not fire a backend request on every keystroke.
    this.currentPageIndex = 0;
    clearTimeout(this.searchDebounceHandle);
    this.searchDebounceHandle = setTimeout(() => this.loadPapers(), 300);
  }

  onStatusFilterChange(): void {
    this.currentPageIndex = 0;
    this.loadPapers();
  }

  goPrev(): void {
    if (this.currentPageIndex > 0) {
      this.currentPageIndex--;
      this.loadPapers();
    }
  }

  goNext(): void {
    if (this.currentPageIndex < this.totalPages - 1) {
      this.currentPageIndex++;
      this.loadPapers();
    }
  }

  onPageSizeChange(size: string | number): void {
    this.currentPageSize = Number(size);
    this.currentPageIndex = 0;
    this.loadPapers();
  }

  get progressPercentage(): number {
    return this.overallProgressPercentage;
  }

  get resolvedCount(): number {
    return this.totalResolvedPapers;
  }

  isProjectPI(): boolean {
    if (!this.project || !this.userInfo) return false;
    return String(this.project.piUserId) === String(this.userInfo.id);
  }

  canAdvance(): boolean {
    if (this.totalProjectPapers === 0) return false;
    return this.isProjectPI() && this.progressPercentage === 100 && this.isCorrectStage();
  }

  isCorrectStage(): boolean {
    return this.project?.state === ProjectState.FULL_TEXT_RETRIEVAL;
  }

  advanceProject(): void {
    if (!this.canAdvance()) return;
    this.submitting = true;
    this.retrievalService.advanceToFullTextScreening(this.projectId).subscribe({
      next: () => {
        this.toastService.success('Stage Advanced', 'Project has moved to Full-Text Screening!');
        this.submitting = false;
        this.projectService.notifyProjectUpdated();
        this.router.navigate(['/projects', this.projectId, 'full-text-screening']);
      },
      error: (err: any) => {
        console.error('Error advancing project stage:', err);
        const errorMsg =
          err?.error?.message ||
          (typeof err?.error === 'string' ? err.error : 'Please check and try again.');
        this.toastService.error('Failed to advance project', errorMsg);
        this.submitting = false;
      },
    });
  }

  openWorkspace(paper: FullTextPaper): void {
    if (!this.isCorrectStage()) {
      this.selectedPaper = paper;
      this.updatePdfUrl();
      return;
    }
    if (paper.lockedByUserName && paper.lockedByUserName !== this.getCurrentUserFullName()) {
      this.toastService.warning(
        'Paper Locked',
        `This paper is currently locked by ${paper.lockedByUserName}`,
      );
      return;
    }

    if (this.selectedPaper) {
      this.releaseLock(this.selectedPaper);
    }

    this.retrievalService.acquireLock(this.projectId, paper.id).subscribe({
      next: () => {
        this.selectedPaper = paper;
        this.updatePdfUrl();
        this.loadPapers(); // Refresh lock indicators in dashboard list
      },
      error: (err: any) => {
        console.error('Error acquiring lock:', err);
        this.toastService.error(
          'Failed to claim paper workspace',
          'The paper may have been locked by someone else.',
        );
      },
    });
  }

  closeWorkspace(): void {
    if (!this.selectedPaper) return;
    const paper = this.selectedPaper;
    this.selectedPaper = null;
    this.pdfUrl = null;
    this.releaseLock(paper);
  }

  private releaseLock(paper: FullTextPaper): void {
    if (!this.isCorrectStage()) {
      this.loadPapers();
      return;
    }
    this.retrievalService.releaseLock(this.projectId, paper.id).subscribe({
      next: () => {
        this.loadPapers();
      },
      error: (err: any) => {
        console.error('Error releasing lock:', err);
      },
    });
  }

  private updatePdfUrl(): void {
    if (this.selectedPaper && this.selectedPaper.pdfId && this.project) {
      const url = `${this.globals.backendUri}/groups/${this.project.groupId}/pdfs/${this.selectedPaper.pdfId}/content`;
      this.loadingPdf = true;
      this.httpClient.get(url, { responseType: 'blob' }).subscribe({
        next: (blob) => {
          this.pdfUrl = blob;
          this.loadingPdf = false;
        },
        error: (err: any) => {
          console.error('Error fetching PDF blob:', err);
          this.toastService.error(
            'Failed to load PDF file content',
            'Please check permissions or try again.',
          );
          this.pdfUrl = null;
          this.loadingPdf = false;
        },
      });
    } else {
      this.pdfUrl = null;
    }
  }

  verifyPaper(): void {
    if (!this.selectedPaper || !this.isCorrectStage()) return;
    const paper = this.selectedPaper;
    this.retrievalService.verifyPaper(this.projectId, paper.id).subscribe({
      next: () => {
        this.toastService.success('Paper Verified', 'PDF verified successfully.');
        this.selectedPaper = null;
        this.pdfUrl = null;
        this.releaseLock(paper);
        this.loadOverallProgress();
        this.loadPapers();
      },
      error: (err: any) => {
        console.error('Error verifying paper:', err);
        this.toastService.error(
          'Failed to verify paper',
          err?.error || err?.error?.message || 'Verification failed.',
        );
      },
    });
  }

  markUnavailable(): void {
    if (!this.selectedPaper || !this.isCorrectStage()) return;
    const paper = this.selectedPaper;
    this.retrievalService.markPaperUnavailable(this.projectId, paper.id).subscribe({
      next: () => {
        this.toastService.success('Paper Marked Unavailable', 'Paper has been marked unavailable.');
        this.selectedPaper = null;
        this.pdfUrl = null;
        this.releaseLock(paper);
        this.loadOverallProgress();
        this.loadPapers();
      },
      error: (err: any) => {
        console.error('Error marking unavailable:', err);
        this.toastService.error('Failed to mark paper unavailable', 'Action failed.');
      },
    });
  }

  undoDecision(paper: FullTextPaper): void {
    if (!this.isCorrectStage()) return;
    this.retrievalService.undoDecision(this.projectId, paper.id).subscribe({
      next: () => {
        this.toastService.success('Decision Undone', 'Decision reset to unchecked.');
        if (this.selectedPaper && this.selectedPaper.id === paper.id) {
          this.selectedPaper = null;
          this.pdfUrl = null;
        }
        this.loadOverallProgress();
        this.loadPapers();
      },
      error: (err: any) => {
        console.error('Error undoing decision:', err);
        this.toastService.error('Failed to undo decision', 'Action failed.');
      },
    });
  }

  // File Upload Logic
  onFilePick(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (files.length > 0) {
      this.uploadPdfFile(files[0]);
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    if (files.length > 0) {
      this.uploadPdfFile(files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  private uploadPdfFile(file: File): void {
    if (!this.selectedPaper || !this.project || !this.isCorrectStage()) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      this.toastService.error('Invalid File Type', 'Only PDF files are allowed.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      this.toastService.error('File Too Large', 'PDF exceeds maximum size limit of 100 MB.');
      return;
    }

    this.uploadingPdf = true;
    this.retrievalService.uploadPdf(this.projectId, this.selectedPaper.id, file).subscribe({
      next: () => {
        this.toastService.success('PDF Uploaded', 'PDF uploaded and linked successfully.');
        this.uploadingPdf = false;
        this.loadOverallProgress();
        this.loadPapers();
      },
      error: (err: any) => {
        console.error('Error uploading PDF:', err);
        this.toastService.error('Upload Failed', 'Failed to upload PDF. Please try again.');
        this.uploadingPdf = false;
      },
    });
  }

  getCurrentUserFullName(): string {
    if (!this.userInfo) return '';
    return `${this.userInfo.firstName} ${this.userInfo.lastName}`;
  }

  formatStatus(status: string): string {
    const map: { [key: string]: string } = {
      PENDING: 'Pending Download',
      DOWNLOADING: 'Downloading...',
      MISSING: 'Missing / No PDF',
      AWAITING_VERIFICATION: 'Awaiting Verification',
      VERIFIED: 'Verified',
      UNAVAILABLE: 'Unavailable',
      FAILED: 'Download Failed',
    };
    return map[status] || status;
  }

  getStatusClass(status: string): string {
    const map: { [key: string]: string } = {
      PENDING: 'badge-pending',
      DOWNLOADING: 'badge-downloading',
      MISSING: 'badge-missing',
      AWAITING_VERIFICATION: 'badge-awaiting',
      VERIFIED: 'badge-verified',
      UNAVAILABLE: 'badge-unavailable',
      FAILED: 'badge-failed',
    };
    return map[status] || 'badge-neutral';
  }

  /** Strip https://doi.org/ prefix and return clean DOI for display. */
  cleanDoi(doi: string | null | undefined): string {
    if (!doi) return 'N/A';
    return doi.replace(/^https?:\/\/doi\.org\//, '');
  }

  /** Build full https://doi.org/ URL from a raw DOI string. */
  doiUrl(doi: string | null | undefined): string {
    if (!doi) return '';
    const cleaned = doi.replace(/^https?:\/\/doi\.org\//, '');
    return `https://doi.org/${cleaned}`;
  }
}
